import { useEffect, useRef, useState } from "react";
import { Camera, CircleHelp, ImagePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { CATALOG_URL, SITE_URL } from "../../config/site";
import SEO from "../../components/SEO";
import FramePhotoEditModal from "../../components/FramePhotoEditModal";
import FrameHowItWorksModal from "../../components/FrameHowItWorksModal";
import FrameRealGallery from "../../components/FrameRealGallery";
import FrameModelLinks from "../../components/FrameModelLinks";
import FrameProductVideo from "../../components/FrameProductVideo";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { useCart } from "../../cart/CartContext";
import { getFrameProductV2 } from "../../catalog/frameProductsV2";
import {
  MARCO_SECUENCIA_COLORS,
  MARCO_SECUENCIA_CONFIG,
} from "../../constants/marcoSecuencia";
import "./MarcoSecuencia.css";

const createEmptySlot = () => ({
  image: null,
  fileName: "",
  position: { x: 0, y: 0 },
  scale: 1,
});

const createInitialSlots = () => {
  return MARCO_SECUENCIA_CONFIG.photoBoxes.reduce((slots, box) => {
    slots[box.id] = createEmptySlot();
    return slots;
  }, {});
};
const MARCO_SECUENCIA_VIEWPORT = {
  x: 0,
  y: 0,
  w: MARCO_SECUENCIA_CONFIG.canvas.w,
  h: MARCO_SECUENCIA_CONFIG.canvas.h,
};

const toViewportBox = (box) => ({
  ...box,
  x: box.x - MARCO_SECUENCIA_VIEWPORT.x,
  y: box.y - MARCO_SECUENCIA_VIEWPORT.y,
});

const MARCO_SECUENCIA_DESKTOP_OVERLAY_BOXES = MARCO_SECUENCIA_CONFIG.photoBoxes.map(toViewportBox);
const MARCO_SECUENCIA_MOBILE_OVERLAY_BOXES = MARCO_SECUENCIA_CONFIG.mobilePhotoBoxes.map(toViewportBox);
const MARCO_SECUENCIA_IMAGE_BOXES = MARCO_SECUENCIA_CONFIG.imageBoxes.map(toViewportBox);

const rotateBoxClockwise = (box) => ({
  ...box,
  x: MARCO_SECUENCIA_VIEWPORT.h - box.y - box.h,
  y: box.x,
  w: box.h,
  h: box.w,
});

const MARCO_SECUENCIA_MOBILE_VIEWPORT = {
  w: MARCO_SECUENCIA_VIEWPORT.h,
  h: MARCO_SECUENCIA_VIEWPORT.w,
};
const MARCO_SECUENCIA_MOBILE_IMAGE_BOXES =
  MARCO_SECUENCIA_IMAGE_BOXES.map(rotateBoxClockwise);
const MARCO_SECUENCIA_VERTICAL_OVERLAY_BOXES =
  MARCO_SECUENCIA_MOBILE_OVERLAY_BOXES.map(rotateBoxClockwise);
const MARCO_SECUENCIA_REAL_GALLERY = [
  {
    src: "/images/frame-gallery/frame-gallery-secuencia/frame-gallery-secuencia-nevera.webp",
    alt: "Marco Secuencia horizontal con cuatro fotografías en una nevera",
  },
  {
    src: "/images/frame-gallery/frame-gallery-secuencia/frame-gallery-secuencia-estanteria.webp",
    alt: "Marco Secuencia negro con soporte sobre una estantería",
  },
  {
    src: "/images/frame-gallery/frame-gallery-secuencia/frame-gallery-secuencia-salon.webp",
    alt: "Marco Secuencia blanco decorando una estantería de salón",
  },
];

export default function MarcoSecuencia() {
  const { addItem } = useCart();
  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef(null);
  const pinchStartScaleRef = useRef(1);
  const touchTapRef = useRef(null);
  const mouseTapRef = useRef(null);
  const photoHintTimerRef = useRef(null);

  const [selectedColor, setSelectedColor] = useState(MARCO_SECUENCIA_COLORS[2]);
  const [quantity, setQuantity] = useState(1);
  const [cartMessage, setCartMessage] = useState("");
  const [frameImage, setFrameImage] = useState(null);
  const [activeSlotId, setActiveSlotId] = useState("first");
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [slots, setSlots] = useState(createInitialSlots);
  const [catalogProduct, setCatalogProduct] = useState(null);
  const [isMobileOverlay, setIsMobileOverlay] = useState(false);
  const [photoHintMessage, setPhotoHintMessage] = useState("");
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 920px)");
    const updateOverlayMode = () => setIsMobileOverlay(mediaQuery.matches);

    updateOverlayMode();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateOverlayMode);
    } else {
      mediaQuery.addListener(updateOverlayMode);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", updateOverlayMode);
      } else {
        mediaQuery.removeListener(updateOverlayMode);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      window.clearTimeout(photoHintTimerRef.current);
    };
  }, []);

  const displayViewport = isMobileOverlay
    ? MARCO_SECUENCIA_MOBILE_VIEWPORT
    : MARCO_SECUENCIA_VIEWPORT;
  const displayImageBoxes = isMobileOverlay
    ? MARCO_SECUENCIA_MOBILE_IMAGE_BOXES
    : MARCO_SECUENCIA_IMAGE_BOXES;
  const overlayBoxes = isMobileOverlay
    ? MARCO_SECUENCIA_VERTICAL_OVERLAY_BOXES
    : MARCO_SECUENCIA_DESKTOP_OVERLAY_BOXES;

  const editingBox = MARCO_SECUENCIA_IMAGE_BOXES.find(
    (box) => box.id === editingSlotId,
  );
  const editingLabel = MARCO_SECUENCIA_CONFIG.photoBoxes.find(
    (box) => box.id === editingSlotId,
  )?.label;

  const product =
    catalogProduct || getFrameProductV2(MARCO_SECUENCIA_CONFIG.id);
  const totalPrice = (product?.price || 0) * quantity;
  const hasUploadedPhotos = Object.values(slots).some((slot) => Boolean(slot.image));

  const clampScale = (value) => Math.min(Math.max(value, 1), 4);

  const clampPosition = (x, y, drawWidth, drawHeight, box) => {
    const limitX = Math.max(0, (drawWidth - box.w) / 2);
    const limitY = Math.max(0, (drawHeight - box.h) / 2);

    return {
      x: Math.min(Math.max(x, -limitX), limitX),
      y: Math.min(Math.max(y, -limitY), limitY),
    };
  };

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;

    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
      clientX,
      clientY,
    };
  };

  const getSlotAtPoint = (point) => {
    return displayImageBoxes.find((box) => (
      point.x >= box.x &&
      point.x <= box.x + box.w &&
      point.y >= box.y &&
      point.y <= box.y + box.h
    ));
  };

  const updateSlot = (slotId, updater) => {
    setSlots((current) => ({
      ...current,
      [slotId]: updater(current[slotId]),
    }));
  };

  const resetFramePhotos = () => {
    setSlots(createInitialSlots());
    setActiveSlotId("first");
    setEditingSlotId(null);
    setPhotoHintMessage("");
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
    touchTapRef.current = null;
    window.clearTimeout(photoHintTimerRef.current);
  };

  const openEditor = (slotId) => {
    if (!slots[slotId]?.image) return;

    setActiveSlotId(slotId);
    setEditingSlotId(slotId);
  };

  const confirmEditor = ({ position, scale }) => {
    updateSlot(editingSlotId, (slot) => ({
      ...slot,
      position,
      scale,
    }));
    setEditingSlotId(null);
  };

  const loadSlotFile = (slotId, file) => {
    if (!file) return;

    const image = new Image();

    image.onload = () => {
      updateSlot(slotId, () => ({
        image,
        fileName: file.name,
        position: { x: 0, y: 0 },
        scale: 1,
      }));

      setActiveSlotId(slotId);
      setPhotoHintMessage("Foto añadida. Tócala para ajustarla.");
      window.clearTimeout(photoHintTimerRef.current);
      photoHintTimerRef.current = window.setTimeout(() => {
        setPhotoHintMessage("");
      }, 3000);
    };

    image.src = URL.createObjectURL(file);
  };

  const handleUpload = (slotId, event) => {
    loadSlotFile(slotId, event.target.files?.[0]);
  };

  const handleOverlayDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOverlayDrop = (slotId, event) => {
    event.preventDefault();
    event.stopPropagation();

    loadSlotFile(slotId, event.dataTransfer?.files?.[0]);
  };

  const changeSlotScale = (slotId, value) => {
    updateSlot(slotId, (slot) => ({
      ...slot,
      scale: clampScale(Number(value)),
    }));
  };

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;

    return Math.sqrt(dx * dx + dy * dy);
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const response = await fetch(CATALOG_URL);
        const data = await response.json();
        const foundProduct = data.products?.find(
          (item) => item.id === MARCO_SECUENCIA_CONFIG.id,
        );

        if (foundProduct) {
          setCatalogProduct(foundProduct);
          setQuantity(foundProduct.quantities?.[0] || 1);
        }
      } catch (error) {
        console.error("Error loading Marco Secuencia product", error);
      }
    };

    loadProduct();
  }, []);

  useEffect(() => {
    const image = new Image();

    image.onload = () => {
      setFrameImage(image);
    };

    image.src = `${selectedColor.frameSrc}?v=secuencia-1800x560`;
  }, [selectedColor.frameSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !frameImage) return;

    const ctx = canvas.getContext("2d");

    canvas.width = displayViewport.w;
    canvas.height = displayViewport.h;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (isMobileOverlay) {
      ctx.save();
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(
        frameImage,
        0,
        0,
        MARCO_SECUENCIA_VIEWPORT.w,
        MARCO_SECUENCIA_VIEWPORT.h,
      );
      ctx.restore();
    } else {
      ctx.drawImage(
        frameImage,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    }

    displayImageBoxes.forEach((box) => {
      const slot = slots[box.id];
      if (!slot?.image) return;

      const imgRatio = slot.image.width / slot.image.height;
      const boxRatio = box.w / box.h;

      let drawWidth;
      let drawHeight;

      if (imgRatio > boxRatio) {
        drawHeight = box.h;
        drawWidth = drawHeight * imgRatio;
      } else {
        drawWidth = box.w;
        drawHeight = drawWidth / imgRatio;
      }

      drawWidth *= slot.scale;
      drawHeight *= slot.scale;

      const offsetX = (box.w - drawWidth) / 2;
      const offsetY = (box.h - drawHeight) / 2;
      const limitedPosition = clampPosition(
        slot.position.x,
        slot.position.y,
        drawWidth,
        drawHeight,
        box,
      );

      ctx.save();
      ctx.beginPath();
      ctx.rect(box.x, box.y, box.w, box.h);
      ctx.clip();
      ctx.drawImage(
        slot.image,
        box.x + offsetX + limitedPosition.x,
        box.y + offsetY + limitedPosition.y,
        drawWidth,
        drawHeight,
      );
      ctx.restore();
    });
  }, [displayImageBoxes, displayViewport, frameImage, isMobileOverlay, slots]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const point = getCanvasPoint(event);
      const slot = getSlotAtPoint(point);
      const slotId = slot?.id || activeSlotId;

      if (!slots[slotId]?.image) return;

      setActiveSlotId(slotId);

      const delta = event.deltaY > 0 ? -0.12 : 0.12;
      changeSlotScale(slotId, slots[slotId].scale + delta);
    };

    canvas.addEventListener("wheel", wheelHandler, {
      passive: false,
    });

    return () => {
      canvas.removeEventListener("wheel", wheelHandler);
    };
  }, [activeSlotId, slots]);

  const handlePointerStart = (event) => {
    if (isMobileOverlay && event.type.startsWith("touch")) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    const point = getCanvasPoint(event);
    const slot = getSlotAtPoint(point);

    if (slot) {
      setActiveSlotId(slot.id);
    }

    if (event.type.startsWith("mouse")) {
      mouseTapRef.current = {
        slotId: slot?.id || null,
        startX: point.clientX,
        startY: point.clientY,
        moved: false,
      };
    }

    if (event.touches?.length === 2 && slot && slots[slot.id]?.image) {
      isDraggingRef.current = false;
      pinchDistanceRef.current = getTouchDistance(event.touches);
      pinchStartScaleRef.current = slots[slot.id].scale;
      return;
    }

    if (!slot || !slots[slot.id]?.image) return;

    isDraggingRef.current = true;
    lastPointRef.current = {
      x: point.clientX,
      y: point.clientY,
    };
  };

  const handlePointerMove = (event) => {
    if (isMobileOverlay && event.type.startsWith("touch")) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    if (event.type.startsWith("mouse") && mouseTapRef.current) {
      const point = getCanvasPoint(event);
      const dx = point.clientX - mouseTapRef.current.startX;
      const dy = point.clientY - mouseTapRef.current.startY;

      if (Math.sqrt(dx * dx + dy * dy) > 12) {
        mouseTapRef.current.moved = true;
      }
    }

    if (event.touches?.length === 2 && pinchDistanceRef.current) {
      const currentDistance = getTouchDistance(event.touches);
      const zoomFactor = currentDistance / pinchDistanceRef.current;
      const nextScale = pinchStartScaleRef.current * zoomFactor;

      changeSlotScale(activeSlotId, nextScale);
      return;
    }

    if (!isDraggingRef.current) return;

    const point = getCanvasPoint(event);
    const dx = point.clientX - lastPointRef.current.x;
    const dy = point.clientY - lastPointRef.current.y;

    updateSlot(activeSlotId, (slot) => ({
      ...slot,
      position: {
        x: slot.position.x + dx,
        y: slot.position.y + dy,
      },
    }));

    lastPointRef.current = {
      x: point.clientX,
      y: point.clientY,
    };
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
  };
  const handlePointerEnd = () => {
    const tap = mouseTapRef.current;
    mouseTapRef.current = null;
    stopDragging();

    if (!tap || tap.moved || !tap.slotId || !slots[tap.slotId]?.image) return;

    openEditor(tap.slotId);
  };

  const handleTouchStart = (event) => {
    const point = getCanvasPoint(event);
    const slot = getSlotAtPoint(point);

    touchTapRef.current = {
      slotId: slot?.id || null,
      startX: point.clientX,
      startY: point.clientY,
      moved: false,
    };

    if (slot) {
      setActiveSlotId(slot.id);
    }
  };

  const handleTouchMove = (event) => {
    if (!touchTapRef.current) return;

    const point = getCanvasPoint(event);
    const dx = point.clientX - touchTapRef.current.startX;
    const dy = point.clientY - touchTapRef.current.startY;

    if (Math.sqrt(dx * dx + dy * dy) > 12) {
      touchTapRef.current.moved = true;
    }
  };

  const handleTouchEnd = () => {
    const tap = touchTapRef.current;
    touchTapRef.current = null;
    stopDragging();

    if (!tap || tap.moved || !tap.slotId || !slots[tap.slotId]?.image) return;

    openEditor(tap.slotId);
  };

  const handleTouchCancel = () => {
    touchTapRef.current = null;
    stopDragging();
  };

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      productName: product.name,
      category: product.category,
      type: product.type,
      quantity,
      unitPrice: product.price || 0,
      currency: product.currency || "EUR",
      options: {
        color: {
          label: "Color",
          value: selectedColor.id,
          valueLabel: selectedColor.name,
        },
      },
      assets: [],
      notes: [
        "Las fotos subidas en el editor son solo una vista previa y no forman parte del pedido.",
      ],
    });

    setCartMessage("Marco anadido al carrito.");
  };

  return (
    <>
      <SEO
        title="Marco Secuencia para cuatro imanes personalizados | Imalandia"
        description="Prueba cuatro fotos en línea en un marco Secuencia para imanes de 5x5 cm y crea una pieza decorativa con historia."
        canonical={`${SITE_URL}/marco-secuencia`}
      />

      <Header />

      <main className="marco-secuencia-page">
        <section className="marco-secuencia-layout">
          <div className="marco-secuencia-heading">
            <p>Marco para cuatro imanes 5x5 cm</p>
            <h1>Marco Secuencia</h1>
          </div>

          <div className="marco-secuencia-note">
            <span>
              Las fotos son solo una vista previa. El pedido del marco no
              incluye imanes personalizados.
            </span>
            <button
              type="button"
              className="frame-help-trigger"
              onClick={() => setHelpModalOpen(true)}
            >
              <CircleHelp aria-hidden="true" />
              Como funciona
            </button>
          </div>

          <aside className="marco-secuencia-side marco-secuencia-side--colors">
            <div className="marco-secuencia-card">
              <div className="marco-secuencia-colors">
                {MARCO_SECUENCIA_COLORS.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    className={
                      selectedColor.id === color.id
                        ? "marco-secuencia-color active"
                        : "marco-secuencia-color"
                    }
                    style={{ background: color.swatch }}
                    title={color.name}
                    aria-label={color.name}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
            </div>
          </aside>

          <div className="marco-secuencia-preview">
            <div className="marco-secuencia-canvas-wrap">
              <canvas
                ref={canvasRef}
                width={displayViewport.w}
                height={displayViewport.h}
                role="img"
                aria-label={
                  isMobileOverlay
                    ? "Vista previa vertical del Marco Secuencia con cuatro fotos"
                    : "Vista previa del Marco Secuencia con cuatro fotos"
                }
                onMouseDown={handlePointerStart}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerEnd}
                onMouseLeave={stopDragging}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
              />

              {overlayBoxes.map((box, index) => {
                const hasImage = Boolean(slots[box.id]?.image);
                const overlayBox = hasImage
                  ? isMobileOverlay
                    ? {
                        x: box.x + box.w - 58,
                        y: box.y + 6,
                        w: 46,
                        h: 46,
                      }
                    : {
                        x: box.x + box.w - 53,
                        y: box.y + 5,
                        w: 48,
                        h: 48,
                      }
                  : box;

                return (
                  <label
                    key={box.id}
                    className={
                      hasImage
                        ? "marco-secuencia-drop-zone has-image"
                        : "marco-secuencia-drop-zone"
                    }
                    style={{
                      left: `${(overlayBox.x / displayViewport.w) * 100}%`,
                      top: `${(overlayBox.y / displayViewport.h) * 100}%`,
                      width: `${(overlayBox.w / displayViewport.w) * 100}%`,
                      height: `${(overlayBox.h / displayViewport.h) * 100}%`,
                    }}
                    onClick={(event) => {
                      if (hasImage) {
                        event.preventDefault();
                        openEditor(box.id);
                        return;
                      }

                      setActiveSlotId(box.id);
                    }}
                    onDragOver={handleOverlayDragOver}
                    onDrop={(event) => handleOverlayDrop(box.id, event)}
                  >
                    <span>
                      {hasImage ? (
                        <Camera size={20} strokeWidth={2.5} />
                      ) : (
                        <>
                          <ImagePlus size={22} strokeWidth={2.4} />
                          <strong>Clic aqui</strong>
                          <small>Subir foto</small>
                        </>
                      )}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => handleUpload(box.id, event)}
                    />
                  </label>
                );
              })}
            </div>
            {isMobileOverlay && hasUploadedPhotos && (
              <p className="marco-secuencia-photo-hint" aria-live="polite">
                {photoHintMessage || "Toca una foto para ajustar el encuadre."}
              </p>
            )}
          </div>

            <button
              type="button"
              className="marco-secuencia-reset-all"
              onClick={resetFramePhotos}
              disabled={!hasUploadedPhotos}
            >
              Borrar fotos
            </button>
          <div className="marco-secuencia-bottom">
            <div className="marco-secuencia-card marco-secuencia-order">
              <h2>Pedido</h2>

              <div className="marco-secuencia-order-row">
                <label className="marco-secuencia-quantity">
                  Cantidad
                  <select
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                  >
                    {product.quantities.map((qty) => (
                      <option key={qty} value={qty}>
                        {qty} unidad{qty > 1 ? "es" : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="marco-secuencia-price">
                  <span>{(product.price || 0).toFixed(2)} EUR / unidad</span>
                  <strong>Total: {totalPrice.toFixed(2)} EUR</strong>
                </div>
              </div>

              {cartMessage && (
                <p className="marco-secuencia-cart-message">{cartMessage}</p>
              )}

              <button
                type="button"
                className="marco-secuencia-cart-button"
                onClick={handleAddToCart}
              >
                Anadir al carrito
              </button>

              <Link to="/carrito" className="marco-secuencia-cart-link">
                Ver carrito
              </Link>
            </div>
          </div>

          <div className="marco-secuencia-real-gallery">
            <FrameRealGallery
              images={MARCO_SECUENCIA_REAL_GALLERY}
              title="Tus recuerdos, en primera fila"
              description="Cuatro momentos que merecen verse juntos, en la nevera o en tu rincón favorito."
            />
          </div>
          <FrameProductVideo
            videoSrc="/videos/marco-secuencia.mp4"
            title="Marco Secuencia en accion"
            description="Cuatro momentos en linea para contar una historia de un vistazo."
            label="Video del Marco Secuencia personalizado"
            wide
          />
          <FrameModelLinks currentModel="secuencia" />
        </section>
      </main>

      {editingSlotId && editingBox && (
        <FramePhotoEditModal
          box={editingBox}
          slot={slots[editingSlotId]}
          title={`Ajustar ${editingLabel?.toLowerCase() || "foto"}`}
          onClose={() => setEditingSlotId(null)}
          onConfirm={confirmEditor}
          onChangeImage={(file) => loadSlotFile(editingSlotId, file)}
        />
      )}

      {helpModalOpen && (
        <FrameHowItWorksModal onClose={() => setHelpModalOpen(false)} />
      )}

      <Footer />
    </>
  );
}










































